/**
 * ef-slider-arrows.js
 *
 * Adds prev/next `.ef-marquee__advance` buttons (same styling as the multicolumn
 * marquee advance control) to opt-in sliders:
 *   - `<grid-slider data-ef-arrows>`
 *   - `.hotspots__products-wrap[data-ef-arrows]`
 *
 * Each click scrolls exactly one visible item-slot. Uses scroll-by-width rather
 * than slide offsetLeft so fractional "products per view" (e.g. 2.7) works from
 * the initial position and hidden grid items are ignored.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var INITIALIZED_FLAG = '__efArrowsInitialized';
  var ENABLED_CLASS = 'ef-arrows--enabled';
  var HOST_SELECTOR = '[data-ef-arrows]';

  var CHEVRON_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  function getTrack(host) {
    if (host.tagName && host.tagName.toLowerCase() === 'grid-slider') {
      return host.querySelector('[data-grid-slider]');
    }
    return host.querySelector('.hotspots__products');
  }

  function getSlides(track, host) {
    if (host.tagName && host.tagName.toLowerCase() === 'grid-slider') {
      return track ? track.querySelectorAll('[data-grid-item]') : [];
    }
    return track ? track.querySelectorAll('.hotspots__product') : [];
  }

  function isSlideVisible(slide) {
    if (!slide) return false;
    var style = window.getComputedStyle(slide);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return slide.getBoundingClientRect().width > 0;
  }

  function getVisibleSlides(slides) {
    var visible = [];
    for (var i = 0; i < slides.length; i++) {
      if (isSlideVisible(slides[i])) visible.push(slides[i]);
    }
    return visible;
  }

  function isScrollable(track) {
    return track && track.scrollWidth > track.clientWidth + 2;
  }

  function getItemSlotWidth(slide, track) {
    if (!slide) return 0;
    var rect = slide.getBoundingClientRect();
    var style = window.getComputedStyle(slide);
    var marginRight = parseFloat(style.marginRight) || 0;
    if (marginRight > 0) return rect.width + marginRight;

    var trackStyle = window.getComputedStyle(track);
    var gap = parseFloat(trackStyle.gap) || parseFloat(trackStyle.columnGap) || 0;
    return rect.width + gap;
  }

  function getMaxScroll(track) {
    return Math.max(0, track.scrollWidth - track.clientWidth);
  }

  function hideNativeArrows(host) {
    var arrows = host.querySelector('.slider__arrows');
    if (arrows) arrows.style.display = 'none';
  }

  function setupHost(host) {
    if (!host || host[INITIALIZED_FLAG]) return;

    var track = getTrack(host);
    var slides = getSlides(track, host);
    var visibleSlides = getVisibleSlides(slides);
    if (!track || visibleSlides.length < 2 || !isScrollable(track)) return;

    host[INITIALIZED_FLAG] = true;
    host.classList.add(ENABLED_CLASS);
    hideNativeArrows(host);

    var arrowObserver = new MutationObserver(hideNativeArrows);
    arrowObserver.observe(host, { childList: true, subtree: true });

    var prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'ef-marquee__advance ef-marquee__advance--prev';
    prevBtn.setAttribute('aria-label', 'Show previous item');
    prevBtn.innerHTML = CHEVRON_SVG;

    var nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'ef-marquee__advance ef-marquee__advance--next';
    nextBtn.setAttribute('aria-label', 'Show next item');
    nextBtn.innerHTML = CHEVRON_SVG;

    host.appendChild(prevBtn);
    host.appendChild(nextBtn);

    function refreshSlides() {
      slides = getSlides(track, host);
      visibleSlides = getVisibleSlides(slides);
    }

    function updateButtons() {
      var maxScroll = getMaxScroll(track);
      var atStart = track.scrollLeft <= 2;
      var atEnd = track.scrollLeft >= maxScroll - 2;
      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd;
    }

    function scrollBySlot(direction) {
      refreshSlides();
      if (!visibleSlides.length) return;

      var slot = getItemSlotWidth(visibleSlides[0], track);
      if (!slot) return;

      var maxScroll = getMaxScroll(track);
      var target = track.scrollLeft + direction * slot;
      target = Math.max(0, Math.min(target, maxScroll));

      if (Math.abs(target - track.scrollLeft) < 1) return;

      track.scrollTo({
        left: target,
        behavior: 'smooth',
      });
    }

    prevBtn.addEventListener('click', function () {
      scrollBySlot(-1);
    });

    nextBtn.addEventListener('click', function () {
      scrollBySlot(1);
    });

    track.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', function () {
      refreshSlides();
      updateButtons();
    });
    updateButtons();
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(HOST_SELECTOR).forEach(setupHost);
  }

  function tryInitAll() {
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryInitAll);
  } else {
    tryInitAll();
  }

  if ('MutationObserver' in window) {
    var domObserver = new MutationObserver(function () {
      tryInitAll();
    });
    domObserver.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
})();
